import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, Printer, Search } from 'lucide-react';
import apiService from '../services/apiService';
import { EmptyState, LoadingState, PageHeader, PageShell, Panel } from './ui/PageShell';
import { DataTable, TableShell } from './ui/ActionMenu';
import MonthYearField from './ui/MonthYearField';
import { ui } from './ui/styles';

const formatCompetenciaLabel = (mes, ano) => `${String(mes).padStart(2, '0')}/${ano}`;

const downloadPdfBase64 = (arquivoBase64, nomeArquivo) => {
  const byteCharacters = atob(arquivoBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo || 'relatorio_bpa_transporte.pdf';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 100);
};

const BpaTransporte = () => {
  const [competencia, setCompetencia] = useState('');
  const [embarques, setEmbarques] = useState([]);
  const [procedimentosDisponiveis, setProcedimentosDisponiveis] = useState([]);
  const [procedimentosSelecionados, setProcedimentosSelecionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingImprimir, setLoadingImprimir] = useState(false);
  const [competenciaAtual, setCompetenciaAtual] = useState('');
  const [searched, setSearched] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [agruparPorEstabelecimento, setAgruparPorEstabelecimento] = useState(false);

  const parseCompetencia = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      throw new Error('Selecione a competência');
    }

    const monthMatch = trimmed.match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
      const ano = Number(monthMatch[1]);
      const mes = Number(monthMatch[2]);
      if (mes < 1 || mes > 12) {
        throw new Error('Competência inválida');
      }
      return { mes, ano };
    }

    const slashMatch = trimmed.match(/^(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const mes = Number(slashMatch[1]);
      const ano = Number(slashMatch[2]);
      if (mes < 1 || mes > 12) {
        throw new Error('Competência inválida');
      }
      return { mes, ano };
    }

    throw new Error('Selecione a competência');
  };

  const handleCompetenciaChange = (nextValue) => {
    setCompetencia(nextValue);
    setFieldError('');
  };

  const handleBuscar = async () => {
    try {
      const { mes, ano } = parseCompetencia(competencia);
      setFieldError('');
      setLoading(true);
      setSearched(true);
      setCompetenciaAtual(formatCompetenciaLabel(mes, ano));

      const result = await apiService.listTransportBpaBoardings({ mes, ano });
      const lista = result.data?.embarques || [];
      const distintos = result.data?.procedimentosDistintos || [];
      setEmbarques(lista);
      setProcedimentosDisponiveis(distintos);
      setProcedimentosSelecionados(distintos.map((item) => item.codigo));

      if (lista.length === 0) {
        toast('Nenhum embarque encontrado para esta competência.', { icon: 'ℹ️' });
      } else {
        toast.success(result.message || `${lista.length} embarque(s) encontrado(s)`);
      }
    } catch (error) {
      setEmbarques([]);
      setProcedimentosDisponiveis([]);
      setProcedimentosSelecionados([]);
      setSearched(false);
      setCompetenciaAtual('');
      const message = error.message || 'Erro ao buscar embarques';
      setFieldError(message);
      toast.error(message);
      console.error('Erro ao buscar embarques BPA transporte:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProcedimento = (codigo) => {
    setProcedimentosSelecionados((prev) =>
      prev.includes(codigo) ? prev.filter((item) => item !== codigo) : [...prev, codigo]
    );
  };

  const handleMarcarTodos = () => {
    setProcedimentosSelecionados(procedimentosDisponiveis.map((item) => item.codigo));
  };

  const handleDesmarcarTodos = () => {
    setProcedimentosSelecionados([]);
  };

  const handleImprimir = async () => {
    try {
      const { mes, ano } = parseCompetencia(competencia);
      setFieldError('');

      if (procedimentosSelecionados.length === 0) {
        toast.error('Selecione ao menos um procedimento para imprimir.');
        return;
      }

      setLoadingImprimir(true);
      const toastId = toast.loading('Gerando relatório PDF...');

      try {
        const result = await apiService.printTransportBpa({
          mes,
          ano,
          procedimentosCodigos: procedimentosSelecionados,
          agruparPorEstabelecimento,
        });

        if (result.success && result.data?.arquivoBase64) {
          downloadPdfBase64(result.data.arquivoBase64, result.data.nomeArquivo);
          toast.success('Relatório PDF gerado com sucesso!', { id: toastId });
        } else {
          toast.error(result.error || 'Arquivo não disponível', { id: toastId });
        }
      } catch (requestError) {
        toast.error(requestError.message || 'Erro ao gerar relatório', { id: toastId });
        throw requestError;
      }
    } catch (error) {
      const message = error.message || 'Erro ao gerar relatório';
      setFieldError(message);
      if (!loadingImprimir) {
        toast.error(message);
      }
      console.error('Erro ao imprimir BPA transporte:', error);
    } finally {
      setLoadingImprimir(false);
    }
  };

  const handleGerarBpa = async () => {
    try {
      const { mes, ano } = parseCompetencia(competencia);
      setFieldError('');

      if (procedimentosSelecionados.length === 0) {
        toast.error('Selecione ao menos um procedimento para gerar o BPA.');
        return;
      }

      setGenerating(true);

      const result = await apiService.generateTransportBpa({
        mes,
        ano,
        procedimentosCodigos: procedimentosSelecionados,
      });
      toast.success(
        `BPA gerado com sucesso! ${result.data.total_registros} registro(s). ` +
        'Exporte o arquivo em Boletim > Gera BPA.'
      );
    } catch (error) {
      const message = error.message || 'Erro ao gerar BPA';
      setFieldError(message);
      toast.error(message);
      console.error('Erro ao gerar BPA transporte:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleBuscar();
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
  };

  const busy = loading || generating || loadingImprimir;
  const embarquesAgrupados = embarques.reduce((groups, item) => {
    const name = item.facility_name || 'Sem estabelecimento';
    const current = groups.get(name) || [];
    current.push(item);
    groups.set(name, current);
    return groups;
  }, new Map());

  return (
    <PageShell withToaster>
      <PageHeader
        title="BPA Transporte"
        description="Geração de BPA a partir de embarques com corrida vinculada (procedimento 0803010125, quantidade = km ÷ 50)."
      />

      <div className={ui.pageBody}>
        <Panel>
          <form className={ui.panelBody} onSubmit={handleSubmit}>
            <div className="max-w-xl">
              <label htmlFor="competencia" className={ui.label}>
                Competência
              </label>
              <MonthYearField
                id="competencia"
                value={competencia}
                onChange={handleCompetenciaChange}
                error={!!fieldError}
                disabled={busy}
              />
              <span className="mt-1 block text-xs text-muted">Selecione o mês e o ano da competência</span>
              {fieldError && <span className="mt-1 block text-xs text-danger">{fieldError}</span>}
            </div>

            <label className="mt-4 flex items-start gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={agruparPorEstabelecimento}
                onChange={(event) => setAgruparPorEstabelecimento(event.target.checked)}
                disabled={busy}
                className="mt-0.5 h-4 w-4 rounded border-border text-primary"
              />
              <span>
                Agrupar relatório por estabelecimento
                <span className="mt-0.5 block text-xs text-muted">
                  A lista e o PDF saem separados pelo estabelecimento de cada embarque.
                </span>
              </span>
            </label>

            <div className={`${ui.filtersActions} mt-4`}>
              <button type="submit" className={ui.btn} disabled={busy}>
                <Search size={16} />
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
              <button
                type="button"
                className={ui.btn}
                onClick={handleImprimir}
                disabled={busy || embarques.length === 0}
              >
                <Printer size={16} />
                {loadingImprimir ? 'Gerando PDF...' : 'Imprimir'}
              </button>
              <button
                type="button"
                className={ui.btnPrimary}
                onClick={handleGerarBpa}
                disabled={busy || embarques.length === 0}
              >
                <FileText size={16} />
                {generating ? 'Gerando BPA...' : 'Gerar BPA'}
              </button>
            </div>
          </form>

          {searched && competenciaAtual && (
            <div className="border-t border-border px-5 py-3 text-sm text-muted">
              Competência <strong className="text-text">{competenciaAtual}</strong> — {embarques.length} embarque(s) elegível(is)
            </div>
          )}

          {searched && procedimentosDisponiveis.length > 0 && (
            <div className="border-t border-border px-5 py-4">
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-text">Procedimentos para o BPA / Impressão</h3>
                  <p className="mt-1 text-xs text-muted">
                    Selecione os códigos que entram no PDF e no arquivo BPA.{' '}
                    {procedimentosSelecionados.length} de {procedimentosDisponiveis.length} selecionado
                    {procedimentosDisponiveis.length !== 1 ? 's' : ''}.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={ui.btn} onClick={handleMarcarTodos} disabled={busy}>
                    Marcar todos
                  </button>
                  <button
                    type="button"
                    className={ui.btnGhost}
                    onClick={handleDesmarcarTodos}
                    disabled={busy || procedimentosSelecionados.length === 0}
                  >
                    Desmarcar todos
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {procedimentosDisponiveis.map((proc) => {
                  const checked = procedimentosSelecionados.includes(proc.codigo);
                  const id = `proc-viagem-${proc.codigo}`;
                  return (
                    <label
                      key={proc.codigo}
                      htmlFor={id}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                        checked
                          ? 'border-primary bg-primary-light/60 text-text'
                          : 'border-border bg-white text-muted hover:bg-slate-50'
                      }`}
                    >
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleProcedimento(proc.codigo)}
                        className="h-4 w-4 rounded border-border text-primary"
                      />
                      <span className="min-w-0 flex-1">
                        <strong className="text-text">{proc.codigo}</strong>
                        {' — '}
                        {proc.descricao}
                      </span>
                      <span className="shrink-0 text-xs font-medium text-muted">{proc.quantidade}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <TableShell>
            <DataTable>
              <thead className={ui.tableHead}>
                <tr>
                  <th className={ui.tableHeadCell}>Corrida</th>
                  <th className={ui.tableHeadCell}>Destino</th>
                  <th className={ui.tableHeadCell}>Estabelecimento</th>
                  <th className={ui.tableHeadCell}>Paciente</th>
                  <th className={ui.tableHeadCell}>Acompanhante</th>
                  <th className={ui.tableHeadCell}>Saída</th>
                  <th className={ui.tableHeadCell}>Qtd (km/50)</th>
                </tr>
              </thead>
              <tbody>
                {!searched ? (
                  <tr>
                    <td colSpan="7" className={ui.emptyCell}>
                      <EmptyState message="Selecione a competência e clique em Buscar." />
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td colSpan="7" className={ui.emptyCell}>
                      <LoadingState message="Carregando embarques..." />
                    </td>
                  </tr>
                ) : embarques.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={ui.emptyCell}>
                      <EmptyState message="Nenhum embarque encontrado para a competência." />
                    </td>
                  </tr>
                ) : (
                  embarques.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {agruparPorEstabelecimento && (
                        (index === 0 || embarques[index - 1]?.facility_name !== item.facility_name) && (
                          <tr className="bg-slate-100">
                            <td colSpan="7" className="px-4 py-2 text-sm font-semibold text-text">
                              {item.facility_name || 'Sem estabelecimento'}
                              <span className="ml-2 text-xs font-medium text-muted">
                                {(embarquesAgrupados.get(item.facility_name || 'Sem estabelecimento') || []).length} embarque(s)
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                      <tr className={`${ui.tableRow} ${index % 2 ? ui.tableRowZebra : ''}`}>
                        <td className={ui.tableCell}>{item.ride_id}</td>
                        <td className={ui.tableCell}>{item.destination_city || '-'}</td>
                        <td className={ui.tableCell}>{item.facility_name || 'Sem estabelecimento'}</td>
                        <td className={ui.tableCell}>{item.patient_name}</td>
                        <td className={ui.tableCell}>{item.companion_name || '-'}</td>
                        <td className={ui.tableCell}>{formatDate(item.departure_at)}</td>
                        <td className={ui.tableCell}>{item.quantidade}</td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </DataTable>
          </TableShell>
        </Panel>
      </div>
    </PageShell>
  );
};

export default BpaTransporte;
